import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync } from "../../utilities/coalesceAsync";

const resolveList = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data?.data)) {
        return payload.data.data;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

export const GetDoctors = createAsyncThunk("doctor/GetDoctors", async ({ token, userTypeId, filters }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
        const url = new URL(`${config.apiUrl}/users`);
        if (userTypeId) {
            url.searchParams.set('user_type_id', userTypeId);
        }

        if (filters && typeof filters === 'object') {
            Object.entries(filters).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') {
                    url.searchParams.set(k, String(v));
                }
            });
        }

        if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[GetDoctors] url=', url.toString());
        }

        const data = await coalesceAsync(
            `get-doctors:${url.toString()}`,
            async () => {
                const response = await fetch(url.toString(), {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const body = await response.json();
                if (!response.ok) {
                    const error = new Error(body?.message ?? "Unable to fetch doctors");
                    error.payload = body;
                    throw error;
                }

                return body;
            },
            { ttlMs: 1500 }
        );

        return fulfillWithValue(data);
    } catch (error) {
        if (error?.payload) {
            return rejectWithValue(error.payload);
        }
        return rejectWithValue({ message: error?.message ?? "Unable to fetch doctors" });
    }
});

export const AddDoctor = createAsyncThunk("doctor/AddDoctor", async ({ token, values, userTypeId }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
        const response = await fetch(`${config.apiUrl}/users`, {
            method: "POST",
            body: JSON.stringify({ ...values, user_type_id: userTypeId }),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data);
        }

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue({ message: error.message });
    }
});

export const UpdateDoctor = createAsyncThunk("doctor/UpdateDoctor", async ({ token, id, values, userTypeId }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        const payload = { ...values };
        if (userTypeId) {
            payload.user_type_id = userTypeId;
        }

        const response = await fetch(`${config.apiUrl}/users/${id}`, {
            method: "PUT",
            body: JSON.stringify(payload),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data);
        }

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue({ message: error.message });
    }
});

export const DeleteDoctor = createAsyncThunk("doctor/DeleteDoctor", async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
        const response = await fetch(`${config.apiUrl}/users/${id}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = response.status === 204 ? {} : await response.json();

        if (!response.ok) {
            return rejectWithValue(data);
        }

        return fulfillWithValue({ id, message: data?.message ?? null });
    } catch (error) {
        return rejectWithValue({ message: error.message });
    }
});

export const DoctorSlice = createSlice({
    name: "doctor",
    initialState: {
        doctors: [],
        loading: false,
        error: null,
        successMessage: null,
        selectedDoctor: null
    },
    reducers: {
        selectDoctor: (state, action) => {
            state.selectedDoctor = action.payload
        }
    },

    extraReducers: {

        //GetDoctors actions
        [GetDoctors.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [GetDoctors.fulfilled]: (state, action) => {
            state.loading = false
            state.doctors = resolveList(action.payload)
            state.successMessage = null
            state.error = null

        },
        [GetDoctors.rejected]: (state, action) => {
            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        [AddDoctor.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [AddDoctor.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessage = action.payload.message
            state.error = null
            const created = action.payload.data
            if (created) {
                const current = Array.isArray(state.doctors) ? state.doctors : []
                state.doctors = [...current, created]
            }
        },
        [AddDoctor.rejected]: (state, action) => {
            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        [UpdateDoctor.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [UpdateDoctor.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessage = action.payload.message
            state.error = null
            const updated = action.payload.data
            if (updated) {
                const index = state.doctors.findIndex(doctor => doctor.id === updated.id);
                if (index !== -1) {
                    state.doctors[index] = {
                        ...state.doctors[index],
                        ...updated,
                    };
                }
            }
        },
        [UpdateDoctor.rejected]: (state, action) => {

            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        //DeleteDoctor actions

        [DeleteDoctor.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [DeleteDoctor.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessage = action.payload.message
            state.error = null
            const index = state.doctors.findIndex(({ id }) => id === action.payload.id);
            if (index !== -1) {
                state.doctors.splice(index, 1);
            }
        },
        [DeleteDoctor.rejected]: (state, action) => {

            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },
    }
})

export default DoctorSlice.reducer;
export const { selectDoctor } = DoctorSlice.actions